/**
 * Cell encoding — one byte per grid cell.
 *
 * A cell is a glyph choice plus a colour choice, and both are small: twelve glyph
 * slots and sixteen colour slots. The obvious packing is 3 bits of glyph and 4 of
 * colour, which is what this project originally specified, and it is wrong. Three
 * bits addresses eight glyphs, which is exactly the density ramp with no room for
 * the blank, the eye, the pupil or the fill — so those would have to be faked by
 * reusing a ramp step in a different colour, and the eye would stop being a
 * distinct shape.
 *
 * Four bits each is one whole byte, and a byte is the cheapest thing to index in
 * either language: `bytes[i]` in Solidity, `Uint8Array[i]` here, no shifting across
 * a cell boundary and no seven-bit stride arithmetic. The 3+4 packing would have
 * saved 128 bytes on a 1024-cell grid at the cost of a mask-and-shift on every read
 * and write of every cell, plus the loss of two glyph slots the art actually needs.
 *
 * Layout, high nibble first:
 *
 *   bits 7..4   glyph index  (see `GLYPH_*` below)
 *   bits 3..0   colour slot  (see `SLOT_*` in `palette.ts`)
 *
 * The order is fixed forever: `Const.sol` mirrors these numbers.
 */

import { CELL_COUNT, GRID_COLS, GRID_ROWS } from "../constants.js";
import { RAMP_LEN } from "../data/glyphs.js";
import { SLOT_BG, SLOT_COUNT } from "./palette.js";

/** Density ramp step `i` lives at glyph index `i`, for i in [0, RAMP_LEN). */
export const GLYPH_RAMP = 0;
/** The space character. Draws nothing, so the background shows through. */
export const GLYPH_BLANK = 8;
/** The eye's outer mark. */
export const GLYPH_EYE = 9;
/** The eye's pupil, drawn over the eye. */
export const GLYPH_PUPIL = 10;
/**
 * A solid cell. Not a character at all: the emitters paint the cell's whole rectangle.
 *
 * Every other glyph index is a mark in the token's typeface, and that is the problem this
 * index exists to solve. `tools/legibility.ts` measures contrast *through* the glyph — a
 * cell is ink over the background, so the authored contrast between two colours is pulled
 * toward 1.0 by however much of the cell the mark actually covers, and the nine sets cover
 * between 17.7% (Braille) and 86.4% (Strata) of a cell. A silhouette edge drawn as a ramp
 * step therefore has no guaranteed strength: the same authored colours measured 1.17 worst
 * at ramp step 5 and 1.29 at step 7, against 1.83 as a fill. Coverage 1.0 is the only
 * treatment whose contrast is the authored contrast, on every typeface, which is what makes
 * an edge a guarantee rather than a hope.
 *
 * It costs nothing on chain: the SVG emits merged `<rect>` runs instead of `<text>` runs
 * (cheaper per cell than a glyph), and the player calls `fillRect` instead of `fillText`.
 */
export const GLYPH_FILL = 11;
/** Glyph indices 12..15 are unused and reserved. */
export const GLYPH_COUNT = 12;

/** The empty cell: nothing drawn, background colour. Grids initialise to this. */
export const EMPTY_CELL = (GLYPH_BLANK << 4) | SLOT_BG;

/** Combine a glyph index and a colour slot into one byte. */
export function packCell(glyph: number, slot: number): number {
  return ((glyph & 0x0f) << 4) | (slot & 0x0f);
}

export function cellGlyph(cell: number): number {
  return (cell >> 4) & 0x0f;
}

export function cellSlot(cell: number): number {
  return cell & 0x0f;
}

/** Row-major index. Matches `idx = y * GRID_COLS + x` in `constants.ts`. */
export function cellIndex(x: number, y: number): number {
  return y * GRID_COLS + x;
}

export function inBounds(x: number, y: number): boolean {
  return x >= 0 && x < GRID_COLS && y >= 0 && y < GRID_ROWS;
}

/**
 * A full grid of cells. One `Uint8Array` of `CELL_COUNT` bytes, which is the exact
 * layout the Solidity renderer builds in memory.
 */
export type Grid = Uint8Array;

export function newGrid(): Grid {
  return new Uint8Array(CELL_COUNT).fill(EMPTY_CELL);
}

/** Write a cell if it is on the grid. Off-grid writes are dropped, not clamped. */
export function put(grid: Grid, x: number, y: number, glyph: number, slot: number): void {
  if (!inBounds(x, y)) return;
  grid[cellIndex(x, y)] = packCell(glyph, slot);
}

export function get(grid: Grid, x: number, y: number): number {
  return inBounds(x, y) ? (grid[cellIndex(x, y)] as number) : EMPTY_CELL;
}

/** True when nothing has been drawn into this cell. */
export function isEmpty(grid: Grid, x: number, y: number): boolean {
  return cellGlyph(get(grid, x, y)) === GLYPH_BLANK;
}

/** Structural guard invoked by tests. */
export function validateCellEncoding(): void {
  if (RAMP_LEN > GLYPH_BLANK) throw new Error("the density ramp overruns GLYPH_BLANK");
  if (GLYPH_COUNT > 16) throw new Error("glyph index does not fit in four bits");
  if (SLOT_COUNT > 16) throw new Error("colour slot does not fit in four bits");
  if (CELL_COUNT !== GRID_COLS * GRID_ROWS) throw new Error("CELL_COUNT disagrees with the grid");

  // The four non-ramp indices are distinct, above the ramp, and inside the count. Two of
  // them sharing a number would be a silent aliasing: the eye would render as a pupil, or
  // an edge as a blank, with nothing in the pipeline able to notice.
  const special = [GLYPH_BLANK, GLYPH_EYE, GLYPH_PUPIL, GLYPH_FILL];
  if (new Set(special).size !== special.length) throw new Error("two glyph indices collide");
  for (const g of special) {
    if (g < RAMP_LEN) throw new Error(`glyph index ${g} is inside the density ramp`);
    if (g >= GLYPH_COUNT) throw new Error(`glyph index ${g} is outside GLYPH_COUNT`);
  }
}
